import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdmissionProfileCard } from '../../components/admission/AdmissionProfileCard';
import { AdmissionReadinessCard } from '../../components/admission/AdmissionReadinessCard';
import { AdmissionDetailsDrawer } from '../../components/admission/AdmissionDetailsDrawer';
import { FileAdmissionCells } from '../../components/admission/FileAdmissionCells';
import { PackageCertificationModal } from '../../components/admission/PackageCertificationModal';
import {
  classifyPackage,
  confirmUpload,
  createPackage,
  createUploadSession,
  evaluatePackageAdmission,
  getPackageAdmission,
  listPrograms,
  LOAN_TAPE_ACCEPT,
  uploadFileContent,
  uploadPackageLoanTape,
} from '../../api/originatorApi';
import { formatApiFailure, isStaleAdmissionError, STALE_ADMISSION_MESSAGE } from '../../api/apiError';
import { waitForAdmissionEvaluation, admissionPollConfig } from '../../admission/pollAdmission';
import { useAdmissionPanelSearch } from '../../admission/useAdmissionPanelSearch';
import {
  canCertifyAdmission,
  documentAssessmentById,
  hasUsableAdmissionProfile,
  isEvaluatingAdmission,
  programmeProfileUnavailableMessage,
} from '../../admission/selectors';
import { ADMISSION_READINESS } from '../../admission/labels';

// Figma "Create New Batch" = 2 steps. Data collected is unchanged; only the
// visual grouping differs from the earlier 4-step layout.
const STEPS = [
  { id: 1, label: 'Assign Queues' },
  { id: 2, label: 'Upload Files' },
];

const FILE_STATUS = {
  pending: 'pending',
  uploading: 'uploading',
  confirming: 'confirming',
  complete: 'complete',
  failed: 'failed',
};

const TAPE_STATUS = {
  none: 'none',
  queued: 'queued',
  uploading: 'uploading',
  attached: 'attached',
  failed: 'failed',
};

const focusRing =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#003A70]/30 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50';

const inputClass =
  'w-full rounded-xl border border-[#D1D5DB] bg-white px-6 py-3 text-sm text-[#5A5A5A] placeholder:text-[#8D8D8D] focus:border-[#003A70] focus:outline-none focus:ring-2 focus:ring-[#003A70]/15 disabled:bg-white disabled:text-[#8D8D8D]';
const labelClass = 'block text-sm font-medium text-[#212121]';
const backButtonClass = `inline-flex items-center justify-center rounded-lg border border-[#003A70] bg-[#F8F8F8] px-4 py-3 text-sm font-semibold text-[#003A70] ${focusRing}`;
const nextButtonClass = `inline-flex items-center justify-center rounded-lg bg-[#003A70] px-4 py-3 text-sm font-bold text-white ${focusRing}`;

function Stepper({ step }) {
  return (
    <div className="flex items-center justify-center">
      {STEPS.map((s, i) => {
        const active = s.id === step;
        const done = s.id < step;
        return (
          <div key={s.id} className="flex items-center">
            <div className="flex flex-col items-center">
              <span
                className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold ${
                  active || done
                    ? 'bg-eorig-navy-600 text-white'
                    : 'border border-eorig-border bg-white text-eorig-muted'
                }`}
              >
                {s.id}
              </span>
              <span
                className={`mt-1.5 text-[11px] font-medium ${
                  active ? 'text-eorig-ink' : 'text-eorig-muted'
                }`}
              >
                {s.label}
              </span>
            </div>
            {i < STEPS.length - 1 ? (
              <span
                className={`mx-3 mb-5 h-0.5 w-24 rounded-full sm:w-40 ${
                  done ? 'bg-eorig-navy-600' : 'bg-eorig-border'
                }`}
                aria-hidden
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function fileStatusLabel(status) {
  switch (status) {
    case FILE_STATUS.uploading:
      return 'Uploading…';
    case FILE_STATUS.confirming:
      return 'Confirming…';
    case FILE_STATUS.complete:
      return 'Complete';
    case FILE_STATUS.failed:
      return 'Failed';
    default:
      return 'Queued';
  }
}

function createQueuedFile(file, index) {
  return {
    localId: `${file.name}-${file.size}-${index}-${Date.now()}`,
    file,
    fileName: file.name,
    mimeType: file.type || null,
    sizeBytes: file.size || 0,
    status: FILE_STATUS.pending,
    error: '',
    fileId: null,
    storagePath: null,
    uploadUrl: null,
    expiresAt: null,
    storageAccount: null,
    contentSha256: null,
  };
}

function resolveAdmission(evaluation, fetched) {
  if (fetched?.readiness_status) return fetched;
  if (evaluation?.admission?.readiness_status) return evaluation.admission;
  if (evaluation?.readiness_status) {
    return evaluation.admission || evaluation;
  }
  return fetched || evaluation?.admission || null;
}

export function NewSubmissionPage() {
  const navigate = useNavigate();
  const admissionPanel = useAdmissionPanelSearch();
  const [step, setStep] = useState(1);
  const [programs, setPrograms] = useState([]);
  const [programsLoading, setProgramsLoading] = useState(true);
  const [programId, setProgramId] = useState('');
  const [programLookup, setProgramLookup] = useState(null);
  const [admissionProfile, setAdmissionProfile] = useState(null);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [queuedFiles, setQueuedFiles] = useState([]);
  // Optional loan tape (CSV / XLSX): queued like files, uploaded on submit.
  const [tapeFile, setTapeFile] = useState(null);
  const [tapeStatus, setTapeStatus] = useState(TAPE_STATUS.none);
  const [tapeInfo, setTapeInfo] = useState(null);
  const [tapeError, setTapeError] = useState('');
  const [packageId, setPackageId] = useState(null);
  const [admission, setAdmission] = useState(null);
  const [certifyOpen, setCertifyOpen] = useState(false);
  const [factsSaving, setFactsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitPhase, setSubmitPhase] = useState('');
  const filesRef = useRef(queuedFiles);
  const packageIdRef = useRef(packageId);
  filesRef.current = queuedFiles;
  packageIdRef.current = packageId;

  const profileReady = hasUsableAdmissionProfile({
    admission_profile: admissionProfile,
    admission_blocked_code: programLookup?.admission_blocked_code,
  });

  useEffect(() => {
    let cancelled = false;
    async function loadPrograms() {
      setProgramsLoading(true);
      setError('');
      try {
        const response = await listPrograms();
        if (cancelled) return;
        const items = Array.isArray(response) ? response : response?.items || [];
        setPrograms(items);
        if (items.length === 1) {
          setProgramId(items[0].id);
          setProgramLookup(items[0]);
          setAdmissionProfile(items[0].admission_profile || null);
          if (items[0].admission_blocked_code === 'ADM-001' || !items[0].admission_profile) {
            setError(programmeProfileUnavailableMessage(items[0]));
          }
        }
      } catch (requestError) {
        if (cancelled) return;
        setPrograms([]);
        setError(requestError.message || 'Unable to load programs for this originator.');
      } finally {
        if (!cancelled) {
          setProgramsLoading(false);
        }
      }
    }
    loadPrograms();
    return () => {
      cancelled = true;
    };
  }, []);

  function handleSelectProgram(nextId) {
    setProgramId(nextId);
    const selected = programs.find((program) => program.id === nextId) || null;
    setProgramLookup(selected);
    setAdmissionProfile(selected?.admission_profile || null);
    if (selected && (selected.admission_blocked_code === 'ADM-001' || !selected.admission_profile)) {
      setError(programmeProfileUnavailableMessage(selected));
    } else if (error && /ADM-001/.test(error)) {
      setError('');
    }
  }

  function updateFile(localId, patch) {
    const next = filesRef.current.map((item) =>
      item.localId === localId ? { ...item, ...patch } : item
    );
    filesRef.current = next;
    setQueuedFiles(next);
  }

  function addFiles(fileList) {
    const selected = Array.from(fileList || []).filter(Boolean);
    if (!selected.length) return;
    const next = [
      ...filesRef.current,
      ...selected.map((file, index) =>
        createQueuedFile(file, filesRef.current.length + index)
      ),
    ];
    filesRef.current = next;
    setQueuedFiles(next);
  }

  function handleQueueFiles(event) {
    addFiles(event.target.files);
    event.target.value = '';
  }

  function handleDrop(event) {
    event.preventDefault();
    event.stopPropagation();
    addFiles(event.dataTransfer?.files);
  }

  function handleDragOver(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  function handleQueueTape(event) {
    const [file] = Array.from(event.target.files || []);
    event.target.value = '';
    if (!file) return;
    setTapeFile(file);
    setTapeStatus(TAPE_STATUS.queued);
    setTapeInfo(null);
    setTapeError('');
  }

  function handleRemoveQueuedTape() {
    setTapeFile(null);
    setTapeStatus(TAPE_STATUS.none);
    setTapeInfo(null);
    setTapeError('');
  }

  /** Attach the queued tape to the package. Returns true when nothing is left to do. */
  async function uploadQueuedTape(pkgId) {
    if (!tapeFile || tapeStatus === TAPE_STATUS.attached) return true;
    setTapeStatus(TAPE_STATUS.uploading);
    setTapeError('');
    try {
      const attached = await uploadPackageLoanTape(pkgId, tapeFile);
      setTapeInfo(attached);
      setTapeStatus(TAPE_STATUS.attached);
      return true;
    } catch (requestError) {
      setTapeStatus(TAPE_STATUS.failed);
      setTapeError(requestError.message || 'Loan tape could not be attached.');
      return false;
    }
  }

  async function uploadOneFile(pkgId, localId) {
    const item = filesRef.current.find((file) => file.localId === localId);
    if (!item || !item.file || item.status === FILE_STATUS.complete) {
      return;
    }

    try {
      let fileId = item.fileId;
      let uploadUrl = item.uploadUrl;

      updateFile(localId, { status: FILE_STATUS.uploading, error: '' });

      if (!fileId) {
        const session = await createUploadSession(pkgId, {
          fileName: item.fileName,
          mimeType: item.mimeType,
          sizeBytes: item.sizeBytes,
        });
        fileId = session.id;
        uploadUrl = session.upload_url;
        updateFile(localId, {
          fileId: session.id,
          storagePath: session.storage_path,
          uploadUrl: session.upload_url,
          expiresAt: session.expires_at,
          storageAccount: session.storage_account,
          status: FILE_STATUS.uploading,
          error: '',
        });
      }

      const current = filesRef.current.find((file) => file.localId === localId);
      await uploadFileContent(pkgId, fileId, current.file, {
        uploadUrl,
        contentType: current.mimeType || undefined,
      });

      updateFile(localId, { status: FILE_STATUS.confirming, error: '' });
      const confirmed = await confirmUpload(pkgId, fileId, {});
      if (confirmed.upload_status !== 'COMPLETE') {
        updateFile(localId, {
          status: FILE_STATUS.failed,
          error: 'Upload was not confirmed as complete.',
        });
        return;
      }

      updateFile(localId, {
        status: FILE_STATUS.complete,
        contentSha256: confirmed.content_sha256 || null,
        error: '',
      });
    } catch (requestError) {
      updateFile(localId, {
        status: FILE_STATUS.failed,
        error: requestError.message || 'Upload failed.',
      });
    }
  }

  async function refreshAdmission(pkgId) {
    const latest = await getPackageAdmission(pkgId);
    setAdmission(latest);
    return latest;
  }

  function applySavedAdmission(next) {
    if (!next) return;
    setAdmission(next);
    setCertifyOpen(false);
  }

  async function runAdmissionEvaluation(pkgId, rowVersionOverride) {
    setSubmitPhase('Evaluating admission…');
    setAdmission((current) => ({
      ...(current || {}),
      package_id: pkgId,
      readiness_status: ADMISSION_READINESS.EVALUATING,
    }));
    const rowVersion =
      rowVersionOverride !== undefined && rowVersionOverride !== null
        ? rowVersionOverride
        : admission?.row_version;
    let evaluation;
    try {
      evaluation = await evaluatePackageAdmission(pkgId, {
        rowVersion: rowVersion === undefined || rowVersion === null ? undefined : rowVersion,
      });
    } catch (requestError) {
      if (isStaleAdmissionError(requestError)) {
        setError(STALE_ADMISSION_MESSAGE);
        return refreshAdmission(pkgId);
      }
      throw requestError;
    }

    const immediate = resolveAdmission(evaluation, evaluation?.admission);
    if (isEvaluatingAdmission(immediate) || evaluation.readiness_status === ADMISSION_READINESS.EVALUATING) {
      setAdmission(immediate);
      const settled = await waitForAdmissionEvaluation(
        () => getPackageAdmission(pkgId),
        admissionPollConfig
      );
      setAdmission(settled);
      return settled;
    }
    const latest = immediate?.document_assessments
      ? immediate
      : await getPackageAdmission(pkgId);
    setAdmission(latest);
    return latest;
  }

  function applyAdmissionResult(pkgId, latest) {
    if (canCertifyAdmission(latest)) {
      setCertifyOpen(true);
      return;
    }
    const status = latest?.readiness_status;
    if (
      status === ADMISSION_READINESS.REVIEW_REQUIRED ||
      status === ADMISSION_READINESS.BLOCKED
    ) {
      navigate(`/packages/${pkgId}`);
    }
  }

  async function handleRetryFile(localId) {
    if (isSubmitting) return;
    const item = filesRef.current.find((file) => file.localId === localId);
    if (!item || item.status !== FILE_STATUS.failed) return;

    const pkgId = packageIdRef.current;
    if (!pkgId) {
      updateFile(localId, { status: FILE_STATUS.pending, error: '' });
      setError('');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSubmitPhase('Uploading…');
    try {
      await uploadOneFile(pkgId, localId);
      const failed = filesRef.current.filter((file) => file.status === FILE_STATUS.failed);
      if (failed.length) {
        setError(
          `${failed.length} file(s) failed to upload. Successful files were kept. Retry the failed files before certification.`
        );
      }
    } finally {
      setIsSubmitting(false);
      setSubmitPhase('');
    }
  }

  async function handleSubmitPackage() {
    if (!programId) {
      setError('Select a program before submission.');
      return;
    }
    if (!profileReady) {
      setError(programmeProfileUnavailableMessage(programLookup));
      return;
    }
    if (!filesRef.current.length) {
      setError('Select at least one file before submission.');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setCertifyOpen(false);
    try {
      let pkgId = packageIdRef.current;
      if (!pkgId) {
        setSubmitPhase('Creating package…');
        const created = await createPackage({
          programId: programId.trim(),
          referenceNumber: referenceNumber.trim() || null,
        });
        pkgId = created.id;
        packageIdRef.current = pkgId;
        setPackageId(pkgId);
        if (created.admission_profile) {
          setAdmissionProfile(created.admission_profile);
        }
      }

      setSubmitPhase('Uploading…');
      for (const item of filesRef.current) {
        if (item.status === FILE_STATUS.complete) continue;
        await uploadOneFile(pkgId, item.localId);
      }

      const incomplete = filesRef.current.filter(
        (file) => file.status !== FILE_STATUS.complete
      );
      if (incomplete.length) {
        setError(
          `${incomplete.length} file(s) failed to upload. Successful files were kept. Retry the failed files before certification.`
        );
        return;
      }

      if (tapeFile) {
        setSubmitPhase('Attaching loan tape…');
        const tapeOk = await uploadQueuedTape(pkgId);
        if (!tapeOk) {
          setError(
            'The loan tape could not be attached. Files were kept; fix or remove the tape and submit again.'
          );
          return;
        }
      }

      setSubmitPhase('Classifying…');
      await classifyPackage(pkgId, { package_status: 'CLASSIFIED' });

      const latest = await runAdmissionEvaluation(pkgId);
      applyAdmissionResult(pkgId, latest);
    } catch (requestError) {
      if (requestError.code === 'ADM-001') {
        setError(programmeProfileUnavailableMessage({ admission_blocked_code: 'ADM-001' }));
      } else {
        setError(requestError.message || 'Failed to submit package.');
      }
    } finally {
      setIsSubmitting(false);
      setSubmitPhase('');
    }
  }

  const hasFailedFiles = queuedFiles.some((file) => file.status === FILE_STATUS.failed);
  const submitLabel = isSubmitting
    ? submitPhase || 'Submitting…'
    : hasFailedFiles
      ? 'Retry failed files'
      : packageId
        ? 'Continue submission'
        : 'Submit batch';

  const drawerFile = queuedFiles.find(
    (file) => file.fileId && String(file.fileId) === String(admissionPanel.documentId)
  );

  return (
    <div className="space-y-6">
      <Stepper step={step} />

      {error ? (
        <div className="mx-auto max-w-4xl rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      <div
        className={`mx-auto rounded-2xl border border-[#F1F1F1] bg-white p-8 shadow-[6px_6px_54px_rgba(0,0,0,0.03)] sm:p-10 ${
          step === 2 ? 'max-w-4xl' : 'max-w-xl'
        }`}
      >
        {step === 1 ? (
          <div className="space-y-6">
            <div>
              <label htmlFor="program-id" className={labelClass}>
                Program
              </label>
              <select
                id="program-id"
                className={`mt-1.5 ${inputClass}`}
                value={programId}
                disabled={programsLoading || programs.length === 0}
                onChange={(event) => handleSelectProgram(event.target.value)}
              >
                <option value="">
                  {programsLoading ? 'Loading programs…' : 'Select a program'}
                </option>
                {programs.map((program) => (
                  <option key={program.id} value={program.id}>
                    {program.name}
                    {program.code ? ` (${program.code})` : ''}
                    {program.custodian_organization_name
                      ? ` → ${program.custodian_organization_name}`
                      : ''}
                  </option>
                ))}
              </select>
              {programLookup ? (
                <p className="mt-2.5 flex items-start gap-1 text-xs font-semibold leading-4 text-[#003A70]">
                  <svg
                    viewBox="0 0 16 16"
                    aria-hidden
                    className="mt-0.5 h-4 w-4 shrink-0"
                    fill="none"
                  >
                    <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.5" />
                    <path
                      d="M5.2 8.1 7.1 10l3.7-4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>
                    Program Validated: {programLookup.name}
                    {programLookup.code ? ` (${programLookup.code})` : ''}
                    {programLookup.custodian_organization_name
                      ? ` routes to ${programLookup.custodian_organization_name}`
                      : ''}
                  </span>
                </p>
              ) : !programsLoading && programs.length === 0 ? (
                <p className="mt-1.5 text-[12px] text-eorig-muted">
                  No program is assigned to this originator.
                </p>
              ) : null}
            </div>

            <div>
              <label htmlFor="reference-number" className={labelClass}>
                Reference number
              </label>
              <input
                id="reference-number"
                className={`mt-1.5 ${inputClass}`}
                placeholder="e.g. Monthly tape — B-10479"
                value={referenceNumber}
                onChange={(event) => setReferenceNumber(event.target.value)}
              />
            </div>

            {admissionProfile ? <AdmissionProfileCard profile={admissionProfile} /> : null}

            <p className="text-xs leading-4 text-[#5A5A5A]">
              Classification submits this package for Provider review. Authority
              approval and custody acceptance are not Originator actions.
            </p>

            <div className="flex items-center justify-between pt-2">
              <button type="button" className={backButtonClass} onClick={() => navigate('/packages')}>
                Back
              </button>
              <button
                type="button"
                className={nextButtonClass}
                onClick={() => setStep(2)}
                disabled={!programId || programsLoading || !profileReady}
              >
                Next
              </button>
            </div>
          </div>
        ) : null}

        {step === 2 ? (
          <div className="space-y-4">
            <div>
              <h2 className="text-sm font-semibold text-eorig-ink">Upload files</h2>
              <p className="mt-1 text-[12px] text-eorig-muted">
                Queue files now; they upload when you submit.
              </p>
            </div>

            <AdmissionReadinessCard admission={admission} />

            <div
              className="rounded-xl border-2 border-dashed border-eorig-border bg-eorig-surface px-6 py-10 text-center"
              onDragOver={handleDragOver}
              onDragEnter={handleDragOver}
              onDrop={handleDrop}
            >
              <p className="text-sm font-medium text-eorig-ink">Drop files or browse</p>
              <label className="mt-4 inline-flex cursor-pointer items-center justify-center rounded-full border border-eorig-border bg-white px-4 py-2 text-sm font-semibold text-eorig-ink transition-colors hover:bg-eorig-surface">
                Choose files
                <input type="file" multiple className="sr-only" onChange={handleQueueFiles} />
              </label>
            </div>

            {queuedFiles.length ? (
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-[13px] text-eorig-ink">
                  <caption className="sr-only">Uploaded files</caption>
                  <thead className="text-[11px] uppercase tracking-wide text-eorig-muted">
                    <tr>
                      <th className="px-2 py-2 font-semibold">File</th>
                      <th className="px-2 py-2 font-semibold">Upload status</th>
                      <th className="px-2 py-2 font-semibold">Document role</th>
                      <th className="px-2 py-2 font-semibold">Source form</th>
                      <th className="px-2 py-2 font-semibold">Admission check</th>
                      <th className="px-2 py-2 font-semibold">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {queuedFiles.map((file) => {
                      const assessment = documentAssessmentById(admission, file.fileId);
                      return (
                        <tr key={file.localId} className="border-t border-eorig-border">
                          <td className="px-2 py-2">
                            <span className="block max-w-[12rem] truncate">{file.fileName}</span>
                            <span className="text-[11px] text-eorig-muted">
                              {file.sizeBytes || 0} bytes
                            </span>
                          </td>
                          <td className="px-2 py-2">
                            <span
                              className={
                                file.status === FILE_STATUS.complete
                                  ? 'font-semibold text-emerald-600'
                                  : file.status === FILE_STATUS.failed
                                    ? 'font-semibold text-rose-600'
                                    : 'text-eorig-muted'
                              }
                            >
                              {fileStatusLabel(file.status)}
                            </span>
                            {file.status === FILE_STATUS.failed ? (
                              <button
                                type="button"
                                onClick={() => handleRetryFile(file.localId)}
                                disabled={isSubmitting}
                                className="ml-2 font-semibold text-eorig-navy-600 hover:text-eorig-cyan disabled:opacity-40"
                              >
                                Retry
                              </button>
                            ) : null}
                          </td>
                          <FileAdmissionCells
                            assessment={assessment}
                            disabled={isSubmitting || !file.fileId}
                            onReview={
                              file.fileId
                                ? () => admissionPanel.openPanel(file.fileId)
                                : undefined
                            }
                          />
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-[13px] text-eorig-muted">No files queued yet.</p>
            )}

            <section
              aria-label="Loan tape"
              className="rounded-xl border border-eorig-border bg-white px-4 py-3"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-eorig-ink">Loan tape (optional)</h3>
                  <p className="mt-1 text-[12px] text-eorig-muted">
                    One CSV or XLSX for this package. After extraction, each tape column is
                    compared with the field of the same name and shown under Validation.
                  </p>
                </div>
                <label
                  className={`inline-flex cursor-pointer items-center justify-center rounded-full border border-eorig-border bg-white px-4 py-2 text-sm font-semibold text-eorig-ink transition-colors hover:bg-eorig-surface ${
                    isSubmitting ? 'pointer-events-none opacity-50' : ''
                  }`}
                >
                  {tapeFile ? 'Replace tape' : 'Choose tape'}
                  <input
                    type="file"
                    accept={LOAN_TAPE_ACCEPT}
                    className="sr-only"
                    onChange={handleQueueTape}
                    disabled={isSubmitting}
                    data-testid="loan-tape-input"
                  />
                </label>
              </div>
              {tapeFile ? (
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-[13px]">
                  <div className="min-w-0">
                    <span className="block truncate font-medium text-eorig-ink">{tapeFile.name}</span>
                    <span className="text-[11px] text-eorig-muted">
                      {tapeFile.size || 0} bytes
                      {tapeInfo ? ` · ${tapeInfo.row_count} row(s) · ${tapeInfo.headers.length} column(s)` : ''}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={
                        tapeStatus === TAPE_STATUS.attached
                          ? 'font-semibold text-emerald-600'
                          : tapeStatus === TAPE_STATUS.failed
                            ? 'font-semibold text-rose-600'
                            : 'text-eorig-muted'
                      }
                    >
                      {tapeStatus === TAPE_STATUS.attached
                        ? 'Attached'
                        : tapeStatus === TAPE_STATUS.uploading
                          ? 'Attaching…'
                          : tapeStatus === TAPE_STATUS.failed
                            ? 'Failed'
                            : 'Queued'}
                    </span>
                    {tapeStatus !== TAPE_STATUS.attached ? (
                      <button
                        type="button"
                        onClick={handleRemoveQueuedTape}
                        disabled={isSubmitting}
                        className="font-semibold text-eorig-navy-600 hover:text-eorig-cyan disabled:opacity-40"
                      >
                        Remove
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : null}
              {tapeError ? (
                <p className="mt-2 text-[12px] text-rose-600" role="alert">
                  {tapeError}
                </p>
              ) : null}
            </section>

            {queuedFiles.some((file) => file.status === FILE_STATUS.failed && file.error) ? (
              <ul className="space-y-1 text-[12px] text-rose-600">
                {queuedFiles
                  .filter((file) => file.status === FILE_STATUS.failed && file.error)
                  .map((file) => (
                    <li key={`${file.localId}-error`}>
                      {file.fileName}: {file.error}
                    </li>
                  ))}
              </ul>
            ) : null}

            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                className={backButtonClass}
                onClick={() => setStep(1)}
                disabled={isSubmitting}
              >
                Back
              </button>
              <button
                type="button"
                className={nextButtonClass}
                onClick={handleSubmitPackage}
                disabled={
                  isSubmitting ||
                  factsSaving ||
                  queuedFiles.length === 0 ||
                  isEvaluatingAdmission(admission)
                }
              >
                {submitLabel}
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <AdmissionDetailsDrawer
        open={admissionPanel.open}
        onClose={admissionPanel.closePanel}
        documentId={admissionPanel.documentId}
        packageId={packageId}
        packageAdmission={admission}
        fileName={drawerFile?.fileName}
        onAdmissionUpdated={applySavedAdmission}
        onFactsActivityChange={setFactsSaving}
      />

      <PackageCertificationModal
        open={certifyOpen}
        onClose={() => setCertifyOpen(false)}
        packageId={packageId}
        admission={admission}
        profile={admissionProfile}
        onAdmissionUpdated={applySavedAdmission}
        onCertified={(result) => {
          setCertifyOpen(false);
          if (packageIdRef.current) {
            navigate(`/packages/${packageIdRef.current}`, {
              state: { certification: result },
            });
          }
        }}
        onStale={async (requestError, latest) => {
          setCertifyOpen(false);
          setError(formatApiFailure(requestError));
          if (latest) setAdmission(latest);
          if (!canCertifyAdmission(latest) && packageIdRef.current) {
            try {
              await runAdmissionEvaluation(packageIdRef.current, latest?.row_version);
            } catch (refreshError) {
              setError(formatApiFailure(refreshError, 'Unable to refresh admission.'));
            }
          } else if (!latest && packageIdRef.current) {
            try {
              await refreshAdmission(packageIdRef.current);
            } catch {
              // Keep the certification error even if refresh fails.
            }
          }
        }}
      />
    </div>
  );
}
